import type { ReactNode } from 'react';
import {
  Youtube, Code2, Palette, GraduationCap, Presentation, ShoppingCart, Camera, PenLine,
  Receipt, MessageSquare, BadgeCheck, Clock, BellRing, ShieldCheck, UserRound,
  Building2, FileText, Scale, Stamp, type LucideIcon,
} from 'lucide-react';
import '@/styles/landingL7.css';

/* Shared Level 7 landing sections.
   ---------------------------------------------------------------------------
   The three reference landings (Accounting, Corporate Services, Run & Comply)
   repeat the same four blocks with different copy. They live here once and take
   their content as props — three near-identical copies would drift apart, which
   is exactly the problem the admin pages had.

   The mockups used emoji for every card icon; these use lucide equivalents in a
   tinted tile, per the house rule. */

/* ── Section header ─────────────────────────────────────────────────────── */
export function L7Head({ eyebrow, title, lead }: { eyebrow: string; title: ReactNode; lead?: ReactNode }) {
  return (
    <div className="l7-head">
      <span className="l7-eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      {lead && <p className="l7-lead">{lead}</p>}
    </div>
  );
}

/* ── Who we're built for (8 cards) ──────────────────────────────────────── */
export type Audience = { icon: LucideIcon; title: string; detail: string };

/** The accounting mockup's eight online-income audiences. */
export const ONLINE_AUDIENCES: Audience[] = [
  { icon: Youtube, title: 'Content creators', detail: 'YouTube · Twitch · TikTok · Patreon' },
  { icon: Code2, title: 'Developers', detail: 'Freelance · US clients · SaaS' },
  { icon: Palette, title: 'Designers', detail: 'Illustration · Branding · Etsy' },
  { icon: GraduationCap, title: 'Online educators', detail: 'Hotmart · Udemy · own academies' },
  { icon: Presentation, title: 'Consultants', detail: 'Marketing · Coaching · B2B' },
  { icon: ShoppingCart, title: 'E-commerce', detail: 'Shopify · Amazon · Dropshipping' },
  { icon: Camera, title: 'Photo & video', detail: 'Stock · Weddings · Licensing' },
  { icon: PenLine, title: 'Copywriters', detail: 'Freelance · Substack · Newsletters' },
];

/** Run & Comply covers the three service lines rather than audiences. */
export const RUNCOMPLY_PILLARS: Audience[] = [
  { icon: Building2, title: 'Set up', detail: 'Mainland & free zone · licensing · visas' },
  { icon: FileText, title: 'Stay compliant', detail: 'Bookkeeping · VAT · Corporate Tax' },
  { icon: Scale, title: 'Stay protected', detail: 'Contracts · POA · corporate legal' },
];

/** Corporate Services: what "one team" actually covers. */
export const CORPORATE_PILLARS: Audience[] = [
  { icon: Building2, title: 'Company formation', detail: 'Mainland & free zone · trade licence' },
  { icon: Stamp, title: 'Visas & PRO', detail: 'Residence · employment · family · Golden' },
  { icon: FileText, title: 'Keep it running', detail: 'Renewals · WPS payroll · attestation' },
];

export const CORPORATE_PROMISES: { icon: LucideIcon; label: string }[] = [
  { icon: UserRound, label: 'A named PRO specialist on your file' },
  { icon: Clock, label: 'Straight answers, not call-centre queues' },
  { icon: BellRing, label: 'Renewal and expiry dates tracked for you' },
  { icon: ShieldCheck, label: 'Government fees at cost, never marked up' },
];

export function AudienceGrid({
  eyebrow, title, lead, items, cols = 4,
}: { eyebrow: string; title: ReactNode; lead?: ReactNode; items: Audience[]; cols?: 2 | 3 | 4 }) {
  return (
    <section className="l7-sec l7-sec--paper2">
      <div className="l7-wrap">
        <L7Head eyebrow={eyebrow} title={title} lead={lead} />
        <div className={`l7-grid l7-grid--${cols}`}>
          {items.map(({ icon: Icon, title: t, detail }) => (
            <div className="l7-card" key={t}>
              <span className="l7-ic"><Icon size={20} strokeWidth={2} /></span>
              <h3>{t}</h3>
              <p>{detail}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── How it flows (numbered steps) ──────────────────────────────────────── */
export type Step = { icon: LucideIcon; title: string; body: string };

export const ACCOUNTING_STEPS: Step[] = [
  { icon: Receipt, title: 'You invoice & upload expenses', body: 'From your phone or laptop, in seconds — with tax settings pre-configured for international platforms (Stripe, YouTube, Amazon, App Store).' },
  { icon: MessageSquare, title: 'Your accountant reviews & guides you', body: 'We answer by chat, check your invoices and make sure you never miss a deduction — a real person, not a ticket queue.' },
  { icon: BadgeCheck, title: 'You confirm, we file to the FTA', body: 'See your VAT and Corporate Tax calculated, confirm in one click, and we submit them to the FTA on time.' },
];

export function StepFlow({
  eyebrow, title, lead, steps,
}: { eyebrow: string; title: ReactNode; lead?: ReactNode; steps: Step[] }) {
  return (
    <section className="l7-sec">
      <div className="l7-wrap">
        <L7Head eyebrow={eyebrow} title={title} lead={lead} />
        <div className="l7-grid l7-grid--3">
          {steps.map(({ icon: Icon, title: t, body }, i) => (
            <div className="l7-card" key={t}>
              <div className="l7-step" style={{ marginBottom: 12 }}>
                <span className="n">{i + 1}</span>
                <span className="l7-ic" style={{ marginBottom: 0, width: 38, height: 38 }}>
                  <Icon size={18} strokeWidth={2} />
                </span>
              </div>
              <h3>{t}</h3>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── The human touch ───────────────────────────────────────────────────── */
const HUMAN_PROMISES: { icon: LucideIcon; label: string }[] = [
  { icon: UserRound, label: 'A dedicated, named accountant' },
  { icon: Clock, label: 'Chat replies in under 30 minutes' },
  { icon: BellRing, label: 'Proactive deadline alerts' },
  { icon: ShieldCheck, label: 'Human review on every filing' },
];

/**
 * The "real person, not a ticket number" section.
 *
 * `photo` is a licensed stock frame recovered from the mockup. It is presented as
 * illustrative — no invented name or quote is attached to it, so it can't read as
 * a fabricated client. Swap in a real team photo whenever one is available.
 */
export function HumanTouch({
  eyebrow, title, lead, photo, promises = HUMAN_PROMISES, reverse = false,
}: {
  eyebrow: string; title: ReactNode; lead?: ReactNode; photo: string;
  promises?: { icon: LucideIcon; label: string }[]; reverse?: boolean;
}) {
  const figure = (
    <div className="l7-figure" style={{ aspectRatio: '4 / 5', maxHeight: 520 }}>
      <img src={photo} alt="" loading="lazy" />
    </div>
  );
  return (
    <section className="l7-sec l7-sec--paper">
      <div className="l7-wrap l7-split">
        {reverse && figure}
        <div>
          <span className="l7-eyebrow">{eyebrow}</span>
          <h2 style={{ margin: '16px 0 12px' }}>{title}</h2>
          {lead && <p className="l7-lead">{lead}</p>}
          <ul style={{ listStyle: 'none', margin: '26px 0 0', display: 'grid', gap: 14 }}>
            {promises.map(({ icon: Icon, label }) => (
              <li key={label} style={{ display: 'flex', gap: 12, alignItems: 'center', fontWeight: 600 }}>
                <span className="l7-ic" style={{ marginBottom: 0, width: 36, height: 36, borderRadius: 10 }}>
                  <Icon size={17} strokeWidth={2} />
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>
        {!reverse && figure}
      </div>
    </section>
  );
}

/* ── Proof strip ───────────────────────────────────────────────────────── */
/**
 * Verifiable-claims strip.
 *
 * The mockup filled this with numbers it labelled "(placeholder)" plus a named
 * client quote and a Google rating. Those are not shipped — inventing testimonials
 * or review scores for a firm that files real tax returns is not something to put
 * on a live site. Pass real figures via `stats` and the section renders; pass none
 * and it renders nothing at all.
 */
export function ProofStrip({ eyebrow, title, stats }: {
  eyebrow: string; title: ReactNode; stats: { value: string; label: string }[];
}) {
  if (!stats.length) return null;
  return (
    <section className="l7-sec l7-sec--navy">
      <div className="l7-wrap">
        <L7Head eyebrow={eyebrow} title={title} />
        <div className={`l7-grid l7-grid--${Math.min(stats.length, 4) as 2 | 3 | 4}`}>
          {stats.map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: "'Poppins', system-ui, sans-serif", fontSize: 40, fontWeight: 800, color: 'var(--acc)', letterSpacing: '-.03em' }}>
                {s.value}
              </div>
              <div style={{ fontSize: 14, color: '#C6D2E2', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Built for the UAE: the facts band ─────────────────────────────────── */
export const UAE_FACTS: { value: string; title: string; body: string }[] = [
  { value: '5%', title: 'VAT', body: 'Since 2018 — registration required once taxable supplies pass AED 375,000.' },
  { value: '9%', title: 'Corporate Tax', body: 'Since 2023 — on profit above AED 375,000, filed annually to the FTA.' },
  { value: 'Soon', title: 'E-invoicing', body: 'On the way — we keep your books in shape ahead of the mandate.' },
  { value: '0', title: 'Missed deadlines', body: 'A managed filing calendar keeps you penalty-free, every cycle.' },
];

export function FactsBand({ eyebrow, title, lead, facts = UAE_FACTS }: {
  eyebrow: string; title: ReactNode; lead?: ReactNode; facts?: typeof UAE_FACTS;
}) {
  return (
    <section className="l7-sec l7-sec--paper">
      <div className="l7-wrap">
        <L7Head eyebrow={eyebrow} title={title} lead={lead} />
        <div className="l7-grid l7-grid--4">
          {facts.map(f => (
            <div className="l7-fact" key={f.title}>
              <b>{f.value}</b>
              <h4>{f.title}</h4>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── The human band ────────────────────────────────────────────────────── */
/**
 * The mockup's navy "human touch" band, conversation and all.
 *
 * The exchange is an illustration of how the service works, so it is labelled as
 * one and uses a real member of the team — the mockup's "Marta" doesn't exist,
 * and a named person making a specific savings claim would read as a testimonial
 * rather than an example.
 */
export function HumanBand({
  eyebrow, title, lead, adviser, role, chat, feats = HUMAN_PROMISES.map(p => p.label),
}: {
  eyebrow: string; title: ReactNode; lead: ReactNode;
  adviser: string; role: string; chat: { from: 'them' | 'you'; text: string }[]; feats?: string[];
}) {
  return (
    <section className="l7-sec">
      <div className="l7-wrap">
        <div className="l7-human">
          <div>
            <span className="l7-eyebrow">{eyebrow}</span>
            <h2 style={{ marginTop: 16 }}>{title}</h2>
            <p>{lead}</p>
            <div className="feats">{feats.map(f => <span key={f}>{f}</span>)}</div>
          </div>
          <div className="l7-adv">
            <div className="l7-advhead">
              <span className="av"><UserRound size={24} strokeWidth={2} /></span>
              <span><b>{adviser} · {role}</b><span>Online now</span></span>
            </div>
            <div className="l7-chat">
              {chat.map((m, i) => <div className={`l7-bub ${m.from}`} key={i}>{m.text}</div>)}
            </div>
            <p className="example">Illustration of a typical exchange</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Hero chips for the accounting/home pages — mirrors ONLINE_AUDIENCES. */
export const ONLINE_PILLS: { label: string; dot: string }[] = [
  { label: 'Creators', dot: '#F0776B' },
  { label: 'Developers', dot: '#5B8DEF' },
  { label: 'Designers', dot: '#E85C9A' },
  { label: 'Online educators', dot: '#F0A640' },
  { label: 'Consultants', dot: '#7A4FC0' },
  { label: 'E-commerce', dot: '#15824E' },
  { label: 'SaaS & agencies', dot: '#2E6BD6' },
];
